<template>
  <div class="past-review-examples">
    <div class="examples-content">
      <div v-if="isLoading" class="loading">
        Ladataan aiempia arviointiesimerkkejä...
      </div>

      <div v-else-if="error" class="error">
        <p>Virhe esimerkkien lataamisessa: {{ error }}</p>
        <button @click="fetchExamples" class="retry-button">
          Yritä uudelleen
        </button>
      </div>

      <div
        v-else-if="!editableExamples || editableExamples.trim() === ''"
        class="no-examples"
      >
        <p>Ei aiempia arviointiesimerkkejä saatavilla.</p>
        <button @click="startEditing" class="add-button">
          Lisää esimerkkejä
        </button>
      </div>

      <div v-else class="examples-editor">
        <div class="examples-preview" v-if="!isEditing">
          <div v-html="renderedExamples"></div>
          <button @click="startEditing" class="edit-button">Muokkaa</button>
        </div>

        <div v-else class="examples-edit">
          <textarea
            v-model="editableExamples"
            class="examples-textarea"
            placeholder="Kirjoita aiempia arviointiesimerkkejä tähän..."
          ></textarea>
          <div class="edit-actions">
            <button @click="saveChanges" class="save-button">Tallenna</button>
            <button @click="cancelEditing" class="cancel-button">
              Peruuta
            </button>
            <button @click="resetToDefault" class="reset-button">
              Palauta oletukseen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores";
import { marked } from "marked";
import { computed, defineExpose, onMounted, ref } from "vue";

interface TeacherExamplesResponse {
  teacherExamples: string;
  error?: string;
}

const props = defineProps<{
  assessmentId?: string;
}>();

const authStore = useAuthStore();

const isLoading = ref(false);
const error = ref<string | null>(null);
const originalExamples = ref("");
const editableExamples = ref("");
const isEditing = ref(false);

// Helper functions for profile preferences
const getSavedExamples = (): string | undefined => {
  return authStore.currentProfile?.state?.preferences?.teacherExamples;
};

const updateExamplesPreference = (examples: string | null) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          teacherExamples: examples,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const renderedExamples = computed(() => {
  return marked(editableExamples.value || "");
});

const fetchExamples = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const assessmentId = props.assessmentId;
    if (!assessmentId) {
      throw new Error("Assessment ID is required");
    }
    const response = await fetch(
      `${apiBaseUrl}/teacher-examples/${assessmentId}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Esimerkkien lataus epäonnistui");
    }

    const data = (await response.json()) as TeacherExamplesResponse;

    if (data.error) {
      throw new Error(data.error);
    }

    originalExamples.value = data.teacherExamples;

    // Try to load saved version from profile state
    try {
      const savedExamples = getSavedExamples();
      editableExamples.value = savedExamples || originalExamples.value;
    } catch (err) {
      console.warn(
        "Failed to load teacher examples from profile state, using original:",
        err,
      );
      editableExamples.value = originalExamples.value;
    }
  } catch (err) {
    console.error("Error fetching teacher examples:", err);
    error.value = err instanceof Error ? err.message : "Tuntematon virhe";
  } finally {
    isLoading.value = false;
  }
};

const startEditing = () => {
  isEditing.value = true;
};

const saveChanges = () => {
  // Save to profile state
  updateExamplesPreference(editableExamples.value);
  isEditing.value = false;
};

const cancelEditing = () => {
  // Revert to saved version
  try {
    const savedExamples = getSavedExamples();
    editableExamples.value = savedExamples || originalExamples.value;
  } catch (err) {
    console.warn("Failed to load saved examples, using original:", err);
    editableExamples.value = originalExamples.value;
  }
  isEditing.value = false;
};

const resetToDefault = () => {
  editableExamples.value = originalExamples.value;
  updateExamplesPreference(null);
  isEditing.value = false;
};

// Expose method for external reset (when "Palauta oletustilaan" is clicked)
const resetExamples = () => {
  editableExamples.value = originalExamples.value;
  updateExamplesPreference(null);
  isEditing.value = false;
};

// Expose method to get current examples for API calls
const getCurrentExamples = () => {
  return editableExamples.value;
};

onMounted(() => {
  void fetchExamples();
});

// Expose methods for parent component
defineExpose({
  resetExamples,
  getCurrentExamples,
});
</script>

<style scoped>
.past-review-examples {
  padding: 1rem;
}

.loading,
.error,
.no-examples {
  padding: 2rem;
  text-align: center;
  color: #666;
}

.error {
  color: #dc3545;
}

.no-examples {
  color: #6c757d;
}

.retry-button,
.add-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.retry-button:hover,
.add-button:hover {
  background-color: #0056b3;
}

.examples-preview {
  position: relative;
}

.edit-button {
  margin-top: 1rem;
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
}

.edit-button:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.examples-textarea {
  width: 100%;
  min-height: 400px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 1rem;
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
  resize: vertical;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.save-button {
  padding: 0.5rem 1rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-button:hover {
  background-color: #218838;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background-color: #545b62;
}

.reset-button {
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.reset-button:hover {
  background-color: #c82333;
}

/* Markdown styling */
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4),
:deep(h5),
:deep(h6) {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

:deep(h1) {
  font-size: 1.5rem;
}
:deep(h2) {
  font-size: 1.3rem;
}
:deep(h3) {
  font-size: 1.1rem;
}
:deep(h4) {
  font-size: 1rem;
}

:deep(p) {
  margin: 0.5rem 0;
  line-height: 1.6;
}

:deep(ul),
:deep(ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

:deep(li) {
  margin-bottom: 0.25rem;
}

:deep(strong) {
  font-weight: 600;
}

:deep(em) {
  font-style: italic;
}

:deep(code) {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: "Courier New", monospace;
  font-size: 0.9em;
}

:deep(hr) {
  border: none;
  border-top: 2px solid #e9ecef;
  margin: 2rem 0;
}
</style>
